import { queryOptions } from "@tanstack/react-query";
import { axiosInstance } from "./axios";
import { z } from "zod";

const teamSchema = z.object({
    id: z.number(),
    fullName: z.string(),
    shortName: z.string(),
});

const matchSchema = z.object({
    id: z.number(),
    date: z.string().nullable(),
    status: z.string(),
    homeGoals: z.number(),
    awayGoals: z.number(),
    shotsAgainstHome: z.number(),
    shotsAgainstAway: z.number(),
    homeTeam: teamSchema,
    awayTeam: teamSchema,
});

const tournamentSchema = z.object({
    id: z.number(),
    group: z.string(),
    name: z.string(),
    tournamentType: z.string(),
    year: z.number(),
    originId: z.number(),
    matches: z.array(matchSchema),
});

const playerTournamentSchema = z.object({
    id: z.number(),
    name: z.string(),
    doesPlayerParticipate: z.boolean(),
});

const tournamentsSchema = z.array(tournamentSchema);
const playerTournamentsSchema = z.array(playerTournamentSchema);

const tournamentMatchXgSchema = z.object({
    matchId: z.number(),
    homeXg: z.nullable(z.number()),
    awayXg: z.nullable(z.number()),
    homeKpi: z.nullable(z.number()),
    awayKpi: z.nullable(z.number()),
    homeGoals: z.nullable(z.number()),
    homeSaves: z.nullable(z.number()),
    awayGoals: z.nullable(z.number()),
    awaySaves: z.nullable(z.number()),
    points: z.nullable(z.number()),
});
const tournamentMatchesXgSchema = z.array(tournamentMatchXgSchema);

export type Team = z.infer<typeof teamSchema>;
export type Match = z.infer<typeof matchSchema>;
export type Tournament = z.infer<typeof tournamentSchema>;
export type Tournaments = z.infer<typeof tournamentsSchema>;
export type PlayerTournament = z.infer<typeof playerTournamentSchema>;
export type PlayerTournaments = z.infer<typeof playerTournamentsSchema>;

export type TournamentMatchXg = z.infer<typeof tournamentMatchXgSchema>;
export type TournamentMatchesXg = z.infer<typeof tournamentMatchesXgSchema>;

const fetchTournaments = async (teamId: number) => {
    try {
        const response = await axiosInstance.get(`/${teamId}/tournaments`);
        return tournamentsSchema.parse(response.data);
    } catch (error) {
        throw new Error(`Failed to fetch tournaments data: ${error}`);
    }
};

const fetchPlayerTournaments = async (playerId: number) => {
    try {
        const response = await axiosInstance.get(`/tournaments/${playerId}`);
        return playerTournamentsSchema.parse(response.data);
    } catch (error) {
        throw new Error(`Failed to fetch player tournaments data: ${error}`);
    }
};

export const tournamentsQueryOptions = (teamId: number | undefined) =>
    queryOptions({
        queryKey: ["tournaments", teamId],
        queryFn: () => fetchTournaments(teamId!),
        enabled: !!teamId,
        staleTime: 5 * 60 * 1000,
    });

export const playerTournamentsQueryOptions = (playerId: number) =>
    queryOptions({
        queryKey: ["tournaments", "player", playerId],
        queryFn: () => fetchPlayerTournaments(playerId),
        enabled: !!playerId,
        staleTime: 5 * 60 * 1000,
    });

export const tournamentMatchesXgQueryOptions = (tournamentId: number | undefined, teamId: number | undefined) =>
    queryOptions({
        queryKey: [tournamentId, teamId, "match-xg"],
        queryFn: async () => {
            const response = await axiosInstance.get(`${tournamentId}/${teamId}/analysis/match-xg`);
            return tournamentMatchesXgSchema.parse(response.data);
        },
        enabled: tournamentId != undefined,
        staleTime: 5 * 60 * 1000,
    });

const teamListItemSchema = z.object({
    teamName: z.string(),
    teamId: z.number(),
    totalKpi: z.number(),
    avgTeamKpi: z.number(),
    avgPlayerKpi: z.number(),
    avgGoalieKpi: z.number(),
});

const teamListSchema = z.array(teamListItemSchema);

export type TeamListItem = z.infer<typeof teamListItemSchema>;
export type TeamList = z.infer<typeof teamListSchema>;

export const tournamentTeamsListQueryOptions = (tournamentId: number | undefined) =>
    queryOptions({
        queryKey: ["tournaments", tournamentId, "teams-list"],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/teams-list`);
            return teamListSchema.parse(response.data);
        },
        enabled: tournamentId != undefined,
        staleTime: 5 * 60 * 1000,
    });
